import { Filter, FiltersMap } from '../model';
import { action, computed, makeObservable, reaction } from 'mobx';
import { Experiments, ReportBI } from '../services';
import { MultiCollectionSupport } from './MultiCollectionSupport';
import { ConditionalModalState } from './ConditionalModalState';
import { QueryPendingStateBIReporter } from './QueryPendingStateBIReporter';

interface ToolbarFiltersState {
  readonly panelFilters: (Filter<any> | null | undefined)[];
}

export interface QueryPendingStateParams {
  container: {
    experiments: Experiments;
  };
  toolbar: {
    multi: MultiCollectionSupport<{}>;
    toolbarFiltersState?: ToolbarFiltersState | null;
    reportBi: ReportBI;
  };
}

function createPendingFilter<T>(origin: Filter<T>) {
  return {
    origin,
    pending: origin.clone(),
  };
}

export class QueryPendingState {
  readonly filters;
  readonly container;
  readonly toolbar;
  readonly discardChangesModal = new ConditionalModalState<null>();
  readonly bi;

  get multi() {
    return this.toolbar.multi;
  }

  get toolbarFiltersState() {
    return this.toolbar.toolbarFiltersState;
  }

  constructor(params: QueryPendingStateParams) {
    this.container = params.container;
    this.toolbar = params.toolbar;

    const allFilters = this.multi.collections.reduce((filters, { query }) => {
      const { search, ...queryFilters } = query.filters;
      return { ...filters, ...queryFilters };
    }, {} as FiltersMap);

    this.filters = Object.values(allFilters)
      .filter((filter): filter is Filter<any> => filter != null)
      .map(createPendingFilter);

    this.bi = new QueryPendingStateBIReporter(this);

    makeObservable(this, {
      apply: action,
      revert: action,
      clearSelected: action,
      getApplicableFilter: action,
      hasChanged: computed,
      trackedFilters: computed,
      trackedFiltersCount: computed,
    });
  }

  get trackedFilters() {
    return this.filters.filter(this.shouldTrackFilter());
  }

  get trackedFiltersCount() {
    return this.trackedFilters.reduce(
      (size, { pending }) => size + pending.size,
      0,
    );
  }

  shouldTrackFilter = () => {
    const panelFilters = this.toolbarFiltersState?.panelFilters;

    return ({ origin }: { origin: Filter<any> }) =>
      origin != null && panelFilters?.includes(origin);
  };

  async onCloseFiltersPanelClick() {
    if (!this.hasChanged) {
      return;
    }

    this.discardChangesModal.open(null);
    const { result } = await this.discardChangesModal.modal.promise();
    if (result === 'confirmed') {
      this.bi.discard();
      this.revert();
    } else if (result === 'declined') {
      this.apply();
    }

    return result;
  }

  getApplicableFilter<A>(filter: A) {
    return (
      this.trackedFilters.find(({ origin }) => origin === (filter as unknown))
        ?.pending ?? filter
    );
  }

  revert() {
    this.trackedFilters.forEach(({ pending, origin }) => {
      pending.setValue(origin.value);
    });
  }

  apply() {
    this.bi.apply();
    this.trackedFilters.forEach(({ pending, origin }) => {
      origin.setValue(pending.value);
    });
    this.multi.collections.forEach((collection) => {
      collection.clearResultAndMoveToStart();
    });
  }

  get hasChanged() {
    return this.trackedFilters.some(({ pending, origin }) =>
      pending.hasDiff(origin.value),
    );
  }

  async clearSelected() {
    this.bi.clear();
    this.trackedFilters.forEach(({ pending }) => {
      pending.reset();
    });
  }

  init() {
    const disposers = [
      this.bi.init(),
      ...this.filters.map(({ pending, origin }) =>
        reaction(
          () => origin.value,
          (value) => {
            pending.setValue(value);
          },
          {
            fireImmediately: true,
          },
        ),
      ),
    ];

    return () => {
      disposers.forEach((d) => d());
    };
  }
}
